type WorkerResponse = {
    requestId?: number;
    decompressed?: ArrayBuffer;
    error?: string;
    closed?: boolean;
};

export class GzipDecompressionWorker {
    private static instance: GzipDecompressionWorker | null = null;

    private worker: Worker | null = null;
    private requestId = 0;
    private pending = new Map<number, { resolve: (value: ArrayBuffer) => void; reject: (reason?: unknown) => void }>();

    static getInstance(): GzipDecompressionWorker {
        if (!GzipDecompressionWorker.instance) {
            GzipDecompressionWorker.instance = new GzipDecompressionWorker();
        }
        return GzipDecompressionWorker.instance;
    }

    async decompress(compressedBuffer: ArrayBuffer): Promise<ArrayBuffer> {
        const requestId = ++this.requestId;

        return await new Promise<ArrayBuffer>((resolve, reject) => {
            this.pending.set(requestId, { resolve, reject });
            const payload = { requestId, compressed: compressedBuffer };

            try {
                const worker = this.getOrCreateWorker();
                // Send without transfer list: avoids an extra JS copy (slice) while keeping fallback buffer usable.
                worker.postMessage(payload);
            } catch (error) {
                this.pending.delete(requestId);
                reject(error instanceof Error ? error : new Error('Worker gzip decompression failed'));
                this.resetWorker();
            }
        });
    }

    private getOrCreateWorker(): Worker {
        if (this.worker) return this.worker;

        const worker = new Worker(new URL('./gzip.worker.js?worker', import.meta.url), { type: 'module' });

        worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
            const payload = event.data;

            if (payload.closed) {
                this.rejectAllPending(new Error('Gzip worker closed due to inactivity'));
                this.resetWorker();
                return;
            }

            if (typeof payload?.requestId !== 'number') return;

            const pending = this.pending.get(payload.requestId);
            if (!pending) return;
            this.pending.delete(payload.requestId);

            if (payload.decompressed instanceof ArrayBuffer) {
                pending.resolve(payload.decompressed);
                return;
            }

            pending.reject(new Error(payload.error ?? 'Worker gzip decompression failed'));
        };

        worker.onerror = (event: ErrorEvent) => {
            const error = new Error(event.message || 'Worker gzip decompression failed');
            this.rejectAllPending(error);

            this.resetWorker();
        };
        this.worker = worker;

        return worker;
    }

    private resetWorker() {
        if (!this.worker) return;
        this.worker.onmessage = null;
        this.worker.onerror = null;
        this.worker.terminate();
        this.worker = null;
    }

    private rejectAllPending(error: Error) {
        for (const pending of this.pending.values()) {
            pending.reject(error);
        }
        this.pending.clear();
    }
}
